import { useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, Ticket, Gift } from "lucide-react";
import { Turnstile } from "@marsidev/react-turnstile";
import AuthShell from "@/components/auth/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { getStoredInvite, clearStoredInvite } from "@/lib/referral";
import { TURNSTILE_SITE_KEY } from "@/config";

const SITE_KEY = TURNSTILE_SITE_KEY;

export default function Signup() {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [searchParams] = useSearchParams();
  // One-click invite links (e.g. /signup?code=BLOQ-XXXXXX) prefill the invite field.
  const linkedCode = (searchParams.get("code") || searchParams.get("invite") || searchParams.get("invite_code") || "")
    .trim()
    .toUpperCase();
  const [form, setForm] = useState({
    display_name: "",
    username: "",
    email: "",
    password: "",
    invite_code: linkedCode,
  });
  const [codeFromLink] = useState(!!linkedCode);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(null);
  const widgetRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (SITE_KEY && !token) {
      toast.error("Please complete the verification");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        email: form.email.trim(),
        password: form.password,
        username: form.username.trim(),
        display_name: form.display_name.trim() || undefined,
        invite_code: form.invite_code.trim() || undefined,
        turnstile_token: token || undefined,
      };
      const data = await signup(payload);
      clearStoredInvite();
      if (data.invite_connected_with) {
        toast.success(`Connected with ${data.invite_connected_with.display_name || "your inviter"}`);
      } else {
        toast.success("Account created — welcome to BLOQ Talk");
      }
      navigate("/app/chats");
    } catch (err) {
      const data = err?.response?.data || {};
      const code = data.error || data.code;
      // Bot check failed on the server — reset the widget so the user can retry.
      setToken(null);
      widgetRef.current?.reset();
      // Backend beta gate (§11): when private beta is on, signup needs a BETA-/BLOQ- code.
      if (err?.response?.status === 403 && (code === "beta_required" || /beta/i.test(data.detail || ""))) {
        toast.error("BLOQ Talk is in private beta — join the waitlist or use an invite code.");
        navigate("/");
        return;
      }
      toast.error(data.detail || "Could not create account");
    } finally {
      setLoading(false);
    }
  };

  const field = (id, label, props) => (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-xs uppercase tracking-wider text-bloq-muted">
        {label}
      </Label>
      <Input
        id={id}
        className="h-12 rounded-xl border-white/12 bg-bloq-bg px-4 text-white placeholder:text-bloq-muted focus-visible:ring-bloq-mint"
        data-testid={`signup-${id}-input`}
        {...props}
      />
    </div>
  );

  return (
    <AuthShell
      title="Create account"
      subtitle="Claim your handle and start building trust."
      footer={
        <>
          Already have an account?{" "}
          <Link to="/login" className="font-semibold text-bloq-mint hover:underline" data-testid="go-login-link">
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
        {field("display_name", "Display name", {
          value: form.display_name,
          onChange: set("display_name"),
          placeholder: "Riley Carter",
        })}
        {field("username", "Username", {
          value: form.username,
          onChange: set("username"),
          placeholder: "riley",
          required: true,
        })}
        {field("email", "Email", {
          type: "email",
          value: form.email,
          onChange: set("email"),
          placeholder: "you@wallet.eth",
          required: true,
        })}

        <div className="space-y-2">
          <Label htmlFor="password" className="text-xs uppercase tracking-wider text-bloq-muted">
            Password
          </Label>
          <div className="relative">
            <Input
              id="password"
              type={show ? "text" : "password"}
              required
              value={form.password}
              onChange={set("password")}
              placeholder="Create a strong password"
              className="h-12 rounded-xl border-white/12 bg-bloq-bg px-4 pr-11 text-white placeholder:text-bloq-muted focus-visible:ring-bloq-mint"
              data-testid="signup-password-input"
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-bloq-muted hover:text-white"
              tabIndex={-1}
            >
              {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="invite_code" className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-bloq-muted">
            <Ticket className="h-3.5 w-3.5" /> Invite code <span className="normal-case tracking-normal text-bloq-muted/70">(optional)</span>
          </Label>
          <Input
            id="invite_code"
            value={form.invite_code}
            onChange={set("invite_code")}
            placeholder="BLOQ-XXXXXX"
            className="h-12 rounded-xl border-bloq-mint/25 bg-bloq-mint/5 px-4 font-mono uppercase tracking-widest text-white placeholder:text-bloq-muted focus-visible:ring-bloq-mint"
            data-testid="signup-invite_code-input"
          />
          {codeFromLink && form.invite_code ? (
            <p className="flex items-center gap-1.5 text-xs font-medium text-bloq-mint" data-testid="signup-code-applied">
              <Gift className="h-3.5 w-3.5" /> Invite applied — you and your inviter both get 7 days of BLOQ+ free.
            </p>
          ) : (
            <p className="text-xs text-bloq-muted">
              Have a code? You'll be connected with your inviter instantly.
            </p>
          )}
        </div>

        {SITE_KEY && (
          <div className="flex justify-center" data-testid="signup-turnstile-wrap">
            <Turnstile
              ref={widgetRef}
              siteKey={SITE_KEY}
              options={{ theme: "dark", size: "flexible" }}
              onSuccess={(t) => setToken(t)}
              onExpire={() => {
                setToken(null);
                widgetRef.current?.reset();
              }}
              onError={() => setToken(null)}
              data-testid="signup-turnstile"
            />
          </div>
        )}

        <Button
          type="submit"
          disabled={loading || (SITE_KEY && !token)}
          className="h-13 w-full rounded-full bloq-gradient py-3.5 text-base font-bold text-bloq-bg shadow-mint hover:opacity-90 disabled:opacity-60"
          data-testid="signup-submit-btn"
        >
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create account"}
        </Button>
      </form>
    </AuthShell>
  );
}
